import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Image,
  Button,
  Tag,
  Space,
  Spin,
  Alert,
  Modal,
  Input,
  Descriptions,
  Typography,
  message,
  Badge,
  Popconfirm,
  Timeline,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  StopOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FireOutlined,
  HistoryOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;
const { Title, Text } = Typography;

function UserVerificationDetails() {
  const params = useParams();
  const user_uuid = params.user_uuid || params.user_id || params.id;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [verificationData, setVerificationData] = useState(null);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (user_uuid) {
      fetchVerificationData();
    } else {
      console.error("❌ Aucun user_uuid trouvé");
    }
  }, [user_uuid]);

  const fetchVerificationData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/submit/verification/${user_uuid}`
      );

      if (!response.ok) throw new Error("Erreur API");

      const result = await response.json();

      if (result.data && result.data.length > 0) {
        setVerificationData(result.data[0]);
      } else {
        message.error("Aucune vérification trouvée");
      }
    } catch (error) {
      console.error(error);
      message.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Approuver (gère aussi les statuts rejected)
  const handleApprove = async () => {
    const isResubmission = verificationData?.is_resubmission || false;
    const isRejected = verificationData?.status === "rejected";

    Modal.confirm({
      title: isRejected
        ? "Confirmer la revalidation"
        : isResubmission
        ? "Confirmer l'approbation de la resoumission"
        : "Confirmer l'approbation",
      content: isRejected
        ? `⚠️ Ces documents ont été rejetés ${verificationData.rejection_count} fois. En les approuvant, vous réinitialisez le processus de vérification. Êtes-vous sûr ?`
        : isResubmission
        ? `Cette personne a déjà été rejetée ${verificationData.rejection_count} fois. Êtes-vous sûr de vouloir approuver cette nouvelle tentative ?`
        : `Êtes-vous sûr de vouloir approuver la vérification de ${verificationData?.user?.fullname} ?`,
      okText: "Oui, approuver",
      okType: "primary",
      cancelText: "Annuler",
      icon: isRejected ? (
        <ThunderboltOutlined style={{ color: "#ff4d4f" }} />
      ) : (
        <CheckCircleOutlined />
      ),
      onOk: async () => {
        try {
          setActionLoading(true);

          // Si status = rejected, passer par pending d'abord
          if (isRejected) {
            console.log("📝 Passage en pending avant approbation...");
            await fetch(
              `${process.env.REACT_APP_API_URL}/api/submit/verification/${user_uuid}/update-status`,
              {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  status: "pending",
                  reviewed_by: "admin",
                }),
              }
            );
          }

          // Puis approuver
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/submit/verification/${user_uuid}/update-status`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                status: "approved",
                reviewed_by: "admin",
              }),
            }
          );

          if (!response.ok) throw new Error("Erreur API");

          message.success(
            isRejected
              ? "✅ Documents revalidés et approuvés !"
              : isResubmission
              ? "✅ Resoumission approuvée avec succès !"
              : "✅ Vérification approuvée avec succès"
          );

          await fetchVerificationData();
        } catch (error) {
          console.error(error);
          message.error("Erreur lors de l'approbation");
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  // ✅ Rejeter
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      message.error("Veuillez indiquer une raison pour le rejet");
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/submit/verification/${user_uuid}/update-status`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            status: "rejected",
            rejection_reason: rejectionReason,
            reviewed_by: "admin",
          }),
        }
      );

      if (!response.ok) throw new Error("Erreur API");

      const data = await response.json();

      message.success(data.message || "Vérification rejetée");

      if (data.data?.account_blocked) {
        Modal.warning({
          title: "Compte bloqué",
          content: `Le compte de ${verificationData?.user?.fullname} a été automatiquement bloqué après ${data.data.rejection_count} rejets.`,
        });
      } else if (data.data?.remaining_attempts !== undefined) {
        message.info(`Il reste ${data.data.remaining_attempts} tentative(s)`);
      }

      if (data.warning) {
        message.warning(data.warning);
      }

      setRejectModalVisible(false);
      setRejectionReason("");
      await fetchVerificationData();
    } catch (error) {
      console.error(error);
      message.error("Erreur lors du rejet");
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ Supprimer les photos
  const handleDeleteAllPhotos = async () => {
    try {
      setActionLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/submit/verification/${user_uuid}/delete-all`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) throw new Error("Erreur API");

      const data = await response.json();
      message.success(data.message);

      if (data.data?.rejection_count_kept > 0) {
        message.info(
          `Historique conservé: ${data.data.rejection_count_kept} rejet(s)`
        );
      }

      await fetchVerificationData();
    } catch (error) {
      console.error(error);
      message.error("Erreur lors de la suppression");
    } finally {
      setActionLoading(false);
    }
  };

  const renderStatus = (status, isResubmission = false) => {
    const statusConfig = {
      pending: {
        color: isResubmission ? "#ff4d4f" : "orange",
        icon: isResubmission ? <ReloadOutlined spin /> : <InfoCircleOutlined />,
        text: isResubmission ? "Resoumission en attente" : "En attente",
      },
      approved: {
        color: "green",
        icon: <CheckCircleOutlined />,
        text: "Approuvée",
      },
      rejected: {
        color: "red",
        icon: <CloseCircleOutlined />,
        text: "Rejetée",
      },
      not_submitted: {
        color: "default",
        icon: <InfoCircleOutlined />,
        text: "Non soumise",
      },
    };

    const config = statusConfig[status] || statusConfig.not_submitted;
    return (
      <Tag
        icon={config.icon}
        color={config.color}
        style={{ fontSize: 16, padding: "8px 16px" }}
      >
        {config.text}
      </Tag>
    );
  };

  const renderRejectionCounter = () => {
    if (!verificationData || verificationData.rejection_count === 0)
      return null;

    const remainingAttempts = 3 - verificationData.rejection_count;
    const isLimitReached = verificationData.has_reached_limit;
    const isResubmission = verificationData.is_resubmission;

    return (
      <>
        {isResubmission && (
          <Alert
            message={
              <Space>
                <FireOutlined />
                <Text strong>
                  Resoumission urgente - Tentative{" "}
                  {verificationData.rejection_count + 1}/3
                </Text>
              </Space>
            }
            description={`L'utilisateur a resoumis ses documents après ${verificationData.rejection_count} rejet(s). Cette demande est prioritaire et doit être traitée rapidement.`}
            type="error"
            showIcon
            icon={<ReloadOutlined spin />}
            style={{ marginBottom: 16 }}
            banner
          />
        )}

        <Alert
          message={
            isLimitReached
              ? `Limite de rejets atteinte (${verificationData.rejection_count}/3)`
              : `Historique: ${verificationData.rejection_count}/3 rejet(s)`
          }
          description={
            isLimitReached
              ? "Le compte a été bloqué automatiquement."
              : `Il reste ${remainingAttempts} tentative(s) avant le blocage automatique.`
          }
          type={isLimitReached ? "error" : isResubmission ? "warning" : "info"}
          showIcon
          icon={isLimitReached ? <StopOutlined /> : <WarningOutlined />}
          style={{ marginBottom: 16 }}
        />
      </>
    );
  };

  const renderTimeline = () => {
    const { rejection_count, submitted_at, reviewed_at, is_resubmission } =
      verificationData || {};

    if (rejection_count === 0 && !submitted_at) return null;

    const items = [];

    if (submitted_at) {
      items.push({
        color: is_resubmission ? "red" : "blue",
        dot: is_resubmission ? <ReloadOutlined spin /> : <CalendarOutlined />,
        children: (
          <>
            <Text strong>
              {is_resubmission ? "Resoumission" : "Première soumission"}
            </Text>
            <br />
            <Text type="secondary">
              {new Date(submitted_at).toLocaleString("fr-FR")}
            </Text>
          </>
        ),
      });
    }

    for (let i = 0; i < rejection_count; i++) {
      items.push({
        color: "red",
        dot: <CloseCircleOutlined />,
        children: (
          <>
            <Text strong>Rejet #{i + 1}</Text>
            <br />
            <Text type="secondary">Document(s) refusé(s)</Text>
          </>
        ),
      });
    }

    if (reviewed_at) {
      items.push({
        color: verificationData.status === "approved" ? "green" : "orange",
        dot:
          verificationData.status === "approved" ? (
            <CheckCircleOutlined />
          ) : (
            <ClockCircleOutlined />
          ),
        children: (
          <>
            <Text strong>Dernière révision</Text>
            <br />
            <Text type="secondary">
              {new Date(reviewed_at).toLocaleString("fr-FR")}
            </Text>
          </>
        ),
      });
    }

    return (
      <Card
        title={
          <>
            <HistoryOutlined /> Historique
          </>
        }
        style={{ marginBottom: 24 }}
      >
        <Timeline items={items} mode="left" />
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "100px" }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Chargement des détails...</Text>
        </div>
      </div>
    );
  }

  if (!verificationData) {
    return (
      <Alert
        message="Erreur"
        description="Vérification introuvable"
        type="error"
        showIcon
      />
    );
  }

  const {
    user,
    status,
    photos,
    rejection_reason,
    rejection_count,
    submitted_at,
    reviewed_at,
    reviewed_by,
    is_resubmission,
  } = verificationData;

  // ✅ Déterminer si on peut valider (pending OU rejected avec photos)
  const hasPhotos =
    photos?.profile?.photo &&
    photos?.id_card_front?.card_front &&
    photos?.id_card_back?.card_back;
  const canValidate =
    (status === "pending" || (status === "rejected" && hasPhotos)) &&
    !verificationData.has_reached_limit;

  return (
    <div
      style={{ padding: 24, backgroundColor: "#f5f5f5", minHeight: "100vh" }}
    >
      {/* Header */}
      <Card style={{ marginBottom: 24 }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/verifications")}
            >
              Retour
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              Détails de la vérification
              {is_resubmission && (
                <Tag
                  color="volcano"
                  icon={<FireOutlined />}
                  style={{ marginLeft: 12 }}
                >
                  RESOUMISSION URGENTE
                </Tag>
              )}
            </Title>
          </Space>
          {renderStatus(status, is_resubmission)}
        </Space>
      </Card>

      {/* Alertes */}
      {renderRejectionCounter()}

      {/* Historique */}
      {renderTimeline()}

      {/* Informations utilisateur */}
      <Card title="Informations utilisateur" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Nom complet" span={2}>
            <Space>
              <UserOutlined />
              <Text strong>{user?.fullname}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Téléphone">
            <Space>
              <PhoneOutlined />
              <Text>{user?.phone}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            <Space>
              <MailOutlined />
              <Text>{user?.email || "Non renseigné"}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Statut du compte">
            <Tag
              color={
                user?.status === "active"
                  ? "green"
                  : user?.status === "blocked"
                  ? "red"
                  : "default"
              }
            >
              {user?.status === "active"
                ? "Actif"
                : user?.status === "blocked"
                ? "Bloqué"
                : "Inactif"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Statut de vérification">
            {renderStatus(user?.verification_status)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Documents */}
      <Card title="Documents soumis" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card
              title="Photo de profil"
              hoverable
              cover={
                photos?.profile?.photo ? (
                  <Image
                    src={photos.profile.photo}
                    alt="Photo de profil"
                    style={{ height: 200, objectFit: "cover" }}
                  />
                ) : (
                  <div
                    style={{
                      height: 200,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    <Text type="secondary">Non fournie</Text>
                  </div>
                )
              }
            >
              <Badge
                status={photos?.profile?.photo ? "success" : "default"}
                text={photos?.profile?.photo ? "Fournie" : "Manquante"}
              />
            </Card>
          </Col>

          <Col xs={24} sm={8}>
            <Card
              title="CNI Recto"
              hoverable
              cover={
                photos?.id_card_front?.card_front ? (
                  <Image
                    src={photos.id_card_front.card_front}
                    alt="CNI Recto"
                    style={{ height: 200, objectFit: "cover" }}
                  />
                ) : (
                  <div
                    style={{
                      height: 200,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    <Text type="secondary">Non fournie</Text>
                  </div>
                )
              }
            >
              <Badge
                status={
                  photos?.id_card_front?.card_front ? "success" : "default"
                }
                text={
                  photos?.id_card_front?.card_front ? "Fournie" : "Manquante"
                }
              />
            </Card>
          </Col>

          <Col xs={24} sm={8}>
            <Card
              title="CNI Verso"
              hoverable
              cover={
                photos?.id_card_back?.card_back ? (
                  <Image
                    src={photos.id_card_back.card_back}
                    alt="CNI Verso"
                    style={{ height: 200, objectFit: "cover" }}
                  />
                ) : (
                  <div
                    style={{
                      height: 200,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    <Text type="secondary">Non fournie</Text>
                  </div>
                )
              }
            >
              <Badge
                status={photos?.id_card_back?.card_back ? "success" : "default"}
                text={photos?.id_card_back?.card_back ? "Fournie" : "Manquante"}
              />
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Détails */}
      <Card title="Détails de la vérification" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={1}>
          <Descriptions.Item label="Date de soumission">
            {submitted_at
              ? new Date(submitted_at).toLocaleString("fr-FR")
              : "Non soumise"}
          </Descriptions.Item>
          <Descriptions.Item label="Date de révision">
            {reviewed_at ? new Date(reviewed_at).toLocaleString("fr-FR") : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Révisée par">
            {reviewed_by || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Nombre de rejets">
            <Badge
              count={rejection_count}
              showZero
              style={{
                backgroundColor: rejection_count > 0 ? "#ff4d4f" : "#52c41a",
              }}
            />
          </Descriptions.Item>
          {rejection_reason && (
            <Descriptions.Item label="Raison du rejet">
              <Alert message={rejection_reason} type="error" showIcon />
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* ✅ Actions - Afficher si : (pending OU rejected avec photos) ET pas bloqué */}
      {canValidate && (
        <Card style={{ marginBottom: 16 }}>
          {status === "rejected" && (
            <Alert
              message="Documents rejetés prêts à être revalidés"
              description="L'utilisateur a resoumis de nouveaux documents. Vous pouvez maintenant les approuver ou les rejeter à nouveau."
              type="warning"
              showIcon
              icon={<EyeOutlined />}
              style={{ marginBottom: 16 }}
            />
          )}

          <Space
            size="large"
            style={{ width: "100%", justifyContent: "center" }}
          >
            <Button
              type="primary"
              size="large"
              icon={
                status === "rejected" ? (
                  <ThunderboltOutlined />
                ) : (
                  <CheckCircleOutlined />
                )
              }
              onClick={handleApprove}
              loading={actionLoading}
              style={{ minWidth: 180 }}
            >
              {status === "rejected"
                ? "✅ Revalider et approuver"
                : "Approuver"}
            </Button>
            <Button
              danger
              size="large"
              icon={<CloseCircleOutlined />}
              onClick={() => setRejectModalVisible(true)}
              loading={actionLoading}
              style={{ minWidth: 150 }}
            >
              {status === "rejected" ? "Rejeter à nouveau" : "Rejeter"}
            </Button>
            <Popconfirm
              title="Supprimer toutes les photos ?"
              description="Cette action supprimera toutes les photos mais conservera l'historique des rejets."
              onConfirm={handleDeleteAllPhotos}
              okText="Oui, supprimer"
              cancelText="Annuler"
            >
              <Button
                type="default"
                size="large"
                icon={<DeleteOutlined />}
                loading={actionLoading}
              >
                Supprimer photos
              </Button>
            </Popconfirm>
          </Space>
        </Card>
      )}

      {/* ✅ Messages selon l'état */}
      {status === "rejected" && !hasPhotos && (
        <Card>
          <Alert
            message="En attente de resoumission"
            description="L'utilisateur n'a pas encore resoumis de nouveaux documents. Les boutons d'action seront disponibles dès qu'il soumettra de nouvelles photos."
            type="info"
            showIcon
            icon={<ClockCircleOutlined />}
          />
        </Card>
      )}

      {verificationData.has_reached_limit && (
        <Card>
          <Alert
            message="Compte bloqué"
            description="Ce compte a été automatiquement bloqué après 3 rejets. Contactez le support pour débloquer ce compte."
            type="error"
            showIcon
            icon={<StopOutlined />}
          />
        </Card>
      )}

      {status === "approved" && (
        <Card>
          <Alert
            message="Vérification approuvée"
            description="Cette vérification a été approuvée. Aucune action supplémentaire n'est nécessaire."
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        </Card>
      )}

      {/* Modal de rejet */}
      <Modal
        title="Rejeter la vérification"
        open={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectionReason("");
        }}
        onOk={handleReject}
        confirmLoading={actionLoading}
        okText="Rejeter"
        okButtonProps={{ danger: true }}
        cancelText="Annuler"
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text strong>Raison du rejet (obligatoire) :</Text>
          <TextArea
            rows={4}
            placeholder="Exemple: Photos floues, document illisible, informations incohérentes..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            maxLength={500}
            showCount
          />
          {rejection_count > 0 && (
            <Alert
              message={`Attention: ${rejection_count + 1}ème rejet`}
              description={
                rejection_count === 2
                  ? "Ce sera le dernier rejet avant le blocage automatique du compte."
                  : `Il restera ${
                      2 - rejection_count
                    } tentative(s) après ce rejet.`
              }
              type="warning"
              showIcon
            />
          )}
          {is_resubmission && (
            <Alert
              message="Resoumission"
              description="Cet utilisateur a déjà resoumis ses documents. Assurez-vous que la raison du rejet est claire pour lui permettre de corriger."
              type="info"
              showIcon
            />
          )}
        </Space>
      </Modal>
    </div>
  );
}

export default UserVerificationDetails;
